import type { LandingContent } from "./types";

export const zhHant: LandingContent = {
  locale: "zh-Hant",
  seo: {
    title: "Prismark 專業相機 · 輕量直覺的 iOS 手動相機工具",
    description: "Prismark 是一款輕量而專業的 iOS 相機 App，支援手動曝光與對焦調整、即時直方圖與斑馬紋、自訂 LUT 匯入、雙鏡頭子母畫面錄影與 HDR 影片。"
  },
  nav: {
    features: "功能",
    pro: "Prismark Pro",
    dualCamera: "雙鏡頭",
    screens: "介面",
    download: "下載",
    privacy: "隱私權政策",
    support: "支援",
    language: "EN"
  },
  hero: {
    tag: "iOS 專業相機",
    title: "Prismark",
    headline: "清晰專注的取景框，直覺順手的手動控制。",
    body: "支援 EV、快門、ISO、對焦與白平衡精確微調，配備即時直方圖與斑馬紋過曝提示，完全以 iOS 原生相機框架打造。",
    primaryCta: "下載",
    secondaryCta: "了解功能"
  },
  overview: {
    title: "涵蓋日常拍攝的核心功能",
    subtitle: "拍照、錄影、雙鏡頭子母畫面與快速回看，搭配直覺的手動參數與輔助測光工具，操作自然順手。",
    features: [
      { title: "照片拍攝", body: "快速回應的快門與取景框，支援 EV、快門速度、ISO、手動對焦與白平衡調整。" },
      { title: "影片錄製", body: "支援 4K 與 60fps 規格，在相容機型上提供 10-bit HDR 錄影與即時狀態監看。" },
      { title: "雙鏡頭錄影", body: "在支援的裝置上同時記錄眼前景物與人物反應，子母畫面視窗可拖移調整位置。", badge: "Prismark Pro" },
      { title: "快速回看", body: "拍攝後直接查看完整 EXIF 參數、色彩格式與解析度，無需離開拍攝介面。" }
    ]
  },
  camera: {
    title: "取景框與手動參數控制",
    body: "底部工具列整合了 EV、快門速度、ISO、手動對焦與色溫白平衡。搭配即時亮度直方圖與斑馬紋過曝提示，拍攝前就能準確掌握曝光細節。",
    points: ["格線與水平儀", "即時亮度直方圖", "斑馬紋過曝提示", "點一下對焦與 AE/AF 鎖定", "內建濾鏡可調整強度", "參數工具列依需要展開"]
  },
  video: {
    title: "穩定清晰的影片錄製",
    body: "影片模式下保留清楚的參數狀態回饋與取景構圖線。在相容裝置上支援錄製 10-bit HDR 影片，錄影計時與狀態提示一目了然。",
    points: ["相容機型支援 10-bit HDR 影片錄製", "錄影狀態指示與精確計時", "常用變焦檔位快速切換", "影片錄製補光燈控制"]
  },
  dual: {
    title: "前後鏡頭同時錄影",
    body: "在支援的裝置上，雙鏡頭錄影可同步捕捉眼前畫面與人物反應。子母畫面視窗輕觸即可拖移位置，非常適合 Vlog、講解示範與旅行記錄。",
    points: ["前後鏡頭同步收錄畫面與反應", "子母畫面視窗可自由拖移位置", "產生單一封裝完整的 10-bit HDR 素材", "不需後製多機位拼接"],
    badge: "Prismark Pro"
  },
  gallery: {
    title: "拍攝後就地回看",
    body: "點一下縮圖即可預覽剛拍好的照片與影片，查看完整的 EXIF 拍攝參數、鏡頭焦段與檔案解析度，並可直接流暢播放。"
  },
  pro: {
    eyebrow: "一次買斷",
    title: "Prismark Pro",
    body:
      "其中三項進階功能屬於 Prismark Pro，一次購買永久解鎖。其餘功能都已包含在內，不需要再支付任何費用。這是一次性買斷，不是訂閱，沒有週期扣款，也不需要手動取消。",
    items: [
      { title: "匯入自訂 LUT", body: "支援 .cube 與 Hald PNG 檔案，匯入後與內建濾鏡並列排列，同樣可以滑動調整濾鏡強度。" },
      { title: "ProRAW 與 RAW 輸出", body: "在支援的機型上儲存原始感光元件資料，為後製修圖與調色保留更充裕的動態範圍。" },
      { title: "雙鏡頭子母畫面", body: "兩顆鏡頭同時錄影，在一段 10-bit HDR 影片素材中完整記錄環境畫面與你的反應。" }
    ],
    note:
      "其他功能都不額外收費：手動曝光參數、內建濾鏡、4K 60fps 錄影、HDR 影片、照片浮水印以及構圖輔助線。如果你在 Prismark Pro 推出前就已下載過 App，這三項功能對你維持免費解鎖。"
  },
  screens: {
    title: "簡潔直接的拍攝介面",
    subtitle: "從照片取景、影片錄製、雙鏡頭子母畫面到相簿回看，所有操作都在連貫的取景介面中完成。",
    labels: ["拍照", "錄影", "雙鏡頭", "相簿"]
  },
  philosophy: {
    title: "介面與互動細節",
    cards: [
      { title: "原生框架", body: "以 iOS 原生相機框架開發，啟動迅速，不嵌入第三方分析或廣告 SDK，拍攝體驗可靠穩定。" },
      { title: "依需要叫出參數", body: "取景框預設只保留構圖輔助資訊。需要調整曝光或對焦時點一下滑桿展開，不遮擋主體畫面。" },
      { title: "系統級互動", body: "完整支援深色與淺色外觀、觸覺回饋與雙指捏合縮放手勢，貼合原生 iOS 操作直覺。" }
    ]
  },
  cta: {
    title: "在 App Store 取得 Prismark",
    body: "支援 iPhone 與 iPad。Prismark Pro 為選購的一次性 App 內購買，其餘功能不額外收費。",
    label: "在 App Store 下載"
  },
  footer: {
    tagline: "輕量而專業的 iOS 相機工具。",
    privacy: "隱私權政策",
    terms: "使用條款",
    support: "支援",
    contact: "聯絡",
    productBy: "Axqora 出品。",
    icp: "粤ICP备2021054614号",
    icpHref: "https://beian.miit.gov.cn/"
  }
};
